// Pure presentation helpers for calls (labels for the dock, history and detail).
import type { Call, CallContextCard, Capability, Session } from '@bcio-voice/contracts';
import { isPrivileged } from '@bcio-voice/domain';
import { formatPhone } from '../../lib/format.ts';
import { usable } from '../../lib/capabilities.ts';
import { cleanText, safeLink } from './contextCard.ts';
import { terminationOf } from '../timeline/timeline.ts';
import { callAnswered, transcriptStateText, transcriptsOnly, TRANSCRIPTS_ONLY_TEXT } from '../../call/mediaStatus.ts';

export type NameLookup = (id: string | null | undefined) => string | null;

export type WorkspaceMode = 'caller' | 'operator';

const ADMIN_PERMISSIONS = new Set(['tenant:owner', 'users:roles', 'numbers:read_all']);

/** Roles of the active tenant membership in the current session. */
export function sessionRoles(session: Session | null | undefined): readonly string[] {
  if (!session) return [];
  const active = session.tenant
    ? session.memberships.find(m => m.tenant_id === session.tenant?.id)
    : session.memberships[0];
  return (active ?? session.memberships[0])?.role_ids ?? [];
}

/**
 * Two modes by role:
 * - `caller` mode for role `agent` (the reps)
 * - `operator` mode for `owner`, `admin` and `supervisor`
 */
export function workspaceModeFor(session: Session | null | undefined): WorkspaceMode {
  if (!session) return 'caller';
  const roles = sessionRoles(session);
  if (isPrivileged([...roles])) return 'operator';
  if (roles.length === 1 && roles[0] === 'agent' && session.permissions.some(p => ADMIN_PERMISSIONS.has(p))) {
    return 'operator';
  }
  return 'caller';
}

export function isOperatorMode(session: Session | null | undefined): boolean {
  return workspaceModeFor(session) === 'operator';
}

export function isCallerMode(session: Session | null | undefined): boolean {
  return workspaceModeFor(session) === 'caller';
}

const COMPANY_FIELD_LABEL = /^(?:company|organization|organisation|account|business|employer|firm)$/i;

/** Reads the optional context card attached to a call. */
export function callContextCard(call: Call): CallContextCard | null {
  const card = (call as { context_card?: CallContextCard | null }).context_card;
  return card && typeof card === 'object' ? card : null;
}

export interface CallWho {
  name: string;
  company: string | null;
  number: string | null;
}

/**
 * Who the call is with for list rows and headers: name, company (from the context card when present),
 * and formatted phone number when distinct from the name.
 */
export function callWho(call: Call, selfId: string, userName: NameLookup): CallWho {
  const baseName = partyName(call, selfId, userName);
  const verifiedContactName = cleanText(call.contact_display_name);
  const rawNumber = call.direction === 'inbound'
    ? call.from.e164
    : call.destination.kind === 'pstn'
      ? call.destination.e164
      : call.from.e164;
  const formattedNumber = rawNumber ? formatPhone(rawNumber) : null;
  const card = callContextCard(call);
  let cardName = card ? cleanText(card.title) : '';
  let company: string | null = null;
  if (card && Array.isArray(card.fields)) {
    const match = card.fields.find(f => COMPANY_FIELD_LABEL.test(cleanText(f?.label)));
    if (match) {
      const val = cleanText(match.value);
      if (val) company = val;
    }
  }
  if (!company && card && typeof (card as { company?: unknown }).company === 'string') {
    const val = cleanText((card as { company?: string }).company);
    if (val) company = val;
  }
  if (cardName) {
    const parts = cardName.split(/\s+(?:[—–-]|\u00b7)\s+/);
    if (parts.length >= 2 && parts[0]?.trim() && parts[1]?.trim()) {
      cardName = parts[0].trim();
      if (!company) company = parts.slice(1).join(' - ').trim();
    }
  }
  const name = verifiedContactName || cardName || baseName;
  if (company && company.toLowerCase() === name.toLowerCase()) {
    company = null;
  }
  const number = formattedNumber && formattedNumber !== name ? formattedNumber : null;
  return { name, company, number };
}

export interface CallCrmLink {
  label: string;
  href: string;
  host: string;
}

/** Primary validated https CRM link from the call's context card, or null when none exists. */
export function callCrmLink(call: Call): CallCrmLink | null {
  const card = callContextCard(call);
  if (!card) return null;
  if (Array.isArray(card.links)) {
    for (const link of card.links) {
      const safe = safeLink(link?.url);
      if (!safe) continue;
      const rawLabel = cleanText(link?.label);
      const label = rawLabel && rawLabel.toLowerCase() !== safe.host.toLowerCase() ? rawLabel : 'Open in CRM';
      return { label, href: safe.href, host: safe.host };
    }
  }
  const candidateUrl = (card as { candidate_url?: unknown }).candidate_url;
  if (typeof candidateUrl === 'string') {
    const safe = safeLink(candidateUrl);
    if (safe) return { label: 'Open in CRM', href: safe.href, host: safe.host };
  }
  return null;
}

/**
 * One-line attribution for caller mode: "Ended by: you / them / the network" only when known,
 * or null when unknown/unattributed.
 */
export function endedByLine(call: Call): 'Ended by: you' | 'Ended by: them' | 'Ended by: the network' | null {
  const t = terminationOf(call);
  const nested = (call as { termination?: { side?: string | null; kind?: string | null; confidence?: string | null } | null }).termination;
  const side = t.termination_side ?? nested?.side ?? null;
  const kind = t.termination_kind ?? nested?.kind ?? null;
  const confidence = t.termination_confidence ?? nested?.confidence ?? null;
  if (!side && !kind) return null;
  if (confidence === 'unknown' && (!side || side === 'unknown') && (!kind || kind === 'unknown')) return null;
  if (kind === 'unknown' && (!side || side === 'unknown')) return null;
  if (side === 'agent' || (kind && ['agent_hangup_command', 'agent_hangup', 'agent_device_bye', 'agent_reject', 'agent_cancel'].includes(kind))) {
    return 'Ended by: you';
  }
  if (side === 'remote' || (kind && ['remote_hangup', 'remote_busy', 'remote_no_answer', 'remote_rejected', 'remote_reject', 'remote_cancel', 'caller_abandoned'].includes(kind))) {
    return 'Ended by: them';
  }
  if (kind && ['carrier_failure', 'registration_failure', 'signaling_lost', 'ice_failure', 'dtls_failure', 'media_lost_auto_cleanup'].includes(kind)) {
    return 'Ended by: the network';
  }
  if (side === 'system') return 'Ended by: the network';
  return null;
}

/**
 * Human status words for the live call card and dock ("Ringing", "On a call with Amy", etc.).
 */
export function humanStatusWords(call: Call, selfId: string, name: string): string {
  switch (call.state) {
    case 'created':
    case 'ringing':
      return 'Ringing';
    case 'active':
      return name ? `On a call with ${name}` : 'On a call';
    case 'held':
      return name ? `On hold with ${name}` : 'On hold';
    default:
      return stateLabel(call, selfId, name);
  }
}

/** Formats an E.164 or line number as "Line 669-310-0200". */
export function formatLineHyphen(e164: string | null | undefined): string | null {
  if (!e164) return null;
  const m = /^\+?1?(\d{3})(\d{3})(\d{4})$/.exec(e164.replace(/[\s()-]/g, ''));
  if (m) return `Line ${m[1]}-${m[2]}-${m[3]}`;
  const formatted = formatPhone(e164);
  return formatted ? `Line ${formatted}` : null;
}

export function callLineWords(call: Call, numbers?: readonly { id: string; e164: string }[] | null): string | null {
  const matched = call.from.number_id ? numbers?.find(n => n.id === call.from.number_id) ?? null : null;
  const e164 = matched?.e164 ?? call.from.e164 ?? (call.direction === 'inbound' && call.destination.kind === 'pstn' ? call.destination.e164 : null);
  return formatLineHyphen(e164);
}

export function partyName(call: Call, selfId: string, userName: NameLookup): string {
  const contactName = cleanText(call.contact_display_name);
  if (call.direction === 'inbound') return contactName || call.from.display_name || (call.from.e164 ? formatPhone(call.from.e164) : 'Unknown caller (number withheld)');
  if (call.destination.kind === 'pstn') return contactName || formatPhone(call.destination.e164);
  const destination = call.destination.user_id;
  if (destination !== selfId) return userName(destination) ?? 'Teammate';
  const other = call.participants.find(p => p.user_id && p.user_id !== selfId);
  return userName(other?.user_id) ?? 'Teammate';
}

export function directionLabel(call: Call, selfId: string): string {
  if (call.direction === 'inbound') return 'Inbound';
  if (call.direction === 'outbound') return 'Outbound';
  return call.destination.kind === 'internal' && call.destination.user_id === selfId ? 'Internal, incoming' : 'Internal, outgoing';
}

export function stateLabel(call: Call, selfId: string, name: string): string {
  const mine = !(call.destination.kind === 'internal' && call.destination.user_id === selfId) && call.direction !== 'inbound';
  switch (call.state) {
    case 'created':
      return mine ? 'Connecting your phone…' : 'Setting up…';
    case 'ringing':
      return mine ? `Ringing ${name}…` : `Incoming call from ${name}`;
    case 'active':
      return 'Connected';
    case 'held':
      return 'On hold';
    case 'dropping':
      return 'Voicemail drop requested';
    case 'ended':
      return 'Call ended';
    default:
      return 'Call failed';
  }
}

export function outcomeLabel(call: Call): string {
  if (call.state === 'failed') return 'Failed';
  const rawOutcome = call.outcome as string | null;
  if (rawOutcome === 'no_answer') return 'No answer';
  if (rawOutcome === 'voicemail') return 'Voicemail';
  switch (call.outcome) {
    case 'answered':
      return 'Answered';
    case 'missed':
      return call.direction === 'outbound' ? 'Not connected' : 'Missed';
    case 'busy':
      return 'Busy';
    case 'rejected':
      return 'Declined';
    case 'cancelled':
      return 'Cancelled';
    case 'failed':
      return 'Failed';
    case 'blocked':
      return 'Blocked';
    // Only an explicit completed outcome claims delivery.
    case 'voicemail_dropped':
      return (call as { voicemail_drop?: unknown }).voicemail_drop ? 'Voicemail dropped' : 'Voicemail';
    case 'voicemail_drop_interrupted':
      return 'Voicemail drop interrupted';
    default:
      return call.state === 'ended' ? 'Ended' : call.state === 'dropping' ? 'Voicemail drop in progress' : 'In progress';
  }
}

export interface NodeState {
  text: string;
  tone: 'neutral' | 'live' | 'end' | 'hold' | 'warn';
}

/** Recorder node (UX spec 7.7). */
export function recorderNode(call: Call, cap: Capability | null): NodeState {
  // Transcripts only: transcribed with no recording is a plain state, never "Failed" or "Not set up".
  if (transcriptsOnly(call)) return { text: TRANSCRIPTS_ONLY_TEXT, tone: 'neutral' };
  if (!usable(cap) && (call.recording_state === 'off' || !cap)) return { text: 'Not set up', tone: 'neutral' };
  switch (call.recording_state) {
    case 'off':
      return { text: 'Off', tone: 'neutral' };
    case 'awaiting_consent':
      return { text: 'Waiting for consent', tone: 'warn' };
    case 'recording':
      return { text: 'Recording', tone: 'end' };
    case 'paused':
      return { text: 'Paused', tone: 'hold' };
    case 'processing':
      return { text: 'Processing', tone: 'neutral' };
    case 'ready':
      return { text: 'Saved', tone: 'neutral' };
    default:
      return { text: 'Failed. Call continues, not recorded.', tone: 'warn' };
  }
}

/**
 * Transcription node (UX spec 7.7). Latency is not in the contract yet, so none is shown. Uses the same words as
 * the call-detail status line: an unanswered call had nothing to transcribe, and 'delayed' (where nearly every
 * finished call ends) is neutral. Pass `segments` when the transcript is loaded.
 */
export function transcriptionNode(call: Call, cap: Capability | null, segments?: number): NodeState {
  switch (call.transcription_state) {
    case 'off':
    case 'pending':
    case 'live':
    case 'delayed':
    case 'complete':
      return transcriptStateText(call.transcription_state, usable(cap), { answered: callAnswered(call), segments, finished: call.state === 'ended' || call.state === 'failed' });
    default:
      return { text: 'Transcript failed', tone: 'warn' };
  }
}

/**
 * The live call card's timer: one continuous count from the moment the call started ringing, carrying on through answer
 * and hold, so it never jumps back to 0:00 at pickup. Outbound: created_at, when the call was dialed. Inbound: created_at,
 * when the call arrived. The call projection carries no time for when this agent's own leg started ringing (a participant's
 * joined_at is set at answer, queue_ms only when the queue wait ends), so IVR, queue and transfer time before the agent's
 * phone rang is included until the projection exposes it. A finished call shows its talk time (talk_ms), the figure history
 * and reports use.
 */
export function elapsedMs(call: Call, now: number): number {
  if (call.state === 'ended' || call.state === 'failed') return call.talk_ms;
  const start = Date.parse(call.created_at);
  return Number.isNaN(start) ? 0 : Math.max(0, now - start);
}

/** Call states whose timer ticks: ringing (or still being set up), connected, on hold, or leaving a voicemail drop. */
export const TICKING_STATES: ReadonlySet<Call['state']> = new Set(['created', 'ringing', 'active', 'held', 'dropping']);

/**
 * The lamp for phone readiness (UX spec 3.6, states language): lit when the phone can carry a call,
 * amber while it is getting there or degraded, red when it is offline or failed, idle when it is off.
 */
export function readinessTone(state: string): 'live' | 'warn' | 'end' | 'neutral' {
  if (state === 'ready') return 'live';
  if (state === 'offline' || state === 'failed' || state === 'revoked') return 'end';
  if (state === 'no_permission' || state === 'capability_off' || state === 'stopped') return 'neutral';
  return 'warn';
}
